import { AfterViewInit, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ModalComponent } from '../../../../shared/modal/modal.component';
import { ModalSize } from '../../../../shared/modal/modal-size.enum';
import { TrialAutocompleteComponent } from '../../../../shared/trial-autocomplete/trial-autocomplete.component';
import { TrialAutocomplete } from '../../../../shared/trial-autocomplete/trial-autocomplete.model';
import { PatientAutoCompleteComponent } from '../../../../shared/patient-autocomplete/patient-autocomplete.component';
import { AdminAutoCompleteComponent } from '../../../../shared/admin-autocomplete/admin-autocomplete.component';
import { SelectOption } from '../../../../core/models/select-option.model';

export interface ExpenseFilterState {
  preloads: boolean;
  investigations: boolean;
  onhold: boolean;
  trialId: string;
  patientId: string;
  wipActionedById: string;
}

interface StoredSelection {
  id: string;
  label: string;
}

@Component({
  selector: 'app-expense-filters',
  templateUrl: './expense-filters.component.html',
  styleUrls: ['./expense-filters.component.scss']
})
export class ExpenseFiltersComponent implements AfterViewInit {
  private static readonly TrialStorageKey = 'expense_trial_filter';
  private static readonly PatientStorageKey = 'expense_patient_filter';
  private static readonly WipStorageKey = 'expense_wip_filter';

  @Output() filtersChanged = new EventEmitter<ExpenseFilterState>();

  @ViewChild('filtersModal') filtersModal: ModalComponent;
  @ViewChild('trialAutocomplete') trialAutocomplete: TrialAutocompleteComponent;
  @ViewChild('patientAutocomplete') patientAutocomplete: PatientAutoCompleteComponent;
  @ViewChild('wipAutocomplete') wipAutocomplete: AdminAutoCompleteComponent;

  protected readonly ModalSize = ModalSize;

  filterForm = new FormGroup({
    preloads: new FormControl<boolean>(localStorage.getItem('preloads') === 'true', { nonNullable: true }),
    investigations: new FormControl<boolean>(localStorage.getItem('investigations') === 'true', { nonNullable: true }),
    onhold: new FormControl<boolean>(localStorage.getItem('on_hold') === 'true', { nonNullable: true }),
    trial: new FormControl<string>(ExpenseFiltersComponent.readStoredSelection(ExpenseFiltersComponent.TrialStorageKey)?.id ?? '', { nonNullable: true }),
    patient: new FormControl<string>(ExpenseFiltersComponent.readStoredSelection(ExpenseFiltersComponent.PatientStorageKey)?.id ?? '', { nonNullable: true }),
    wipBy: new FormControl<string>(ExpenseFiltersComponent.readStoredSelection(ExpenseFiltersComponent.WipStorageKey)?.id ?? '', { nonNullable: true })
  });

  static getStoredTrialId(): string {
    return this.readStoredSelection(this.TrialStorageKey)?.id ?? '';
  }

  static getStoredPatientId(): string {
    return this.readStoredSelection(this.PatientStorageKey)?.id ?? '';
  }

  static getStoredWipActionedById(): string {
    return this.readStoredSelection(this.WipStorageKey)?.id ?? '';
  }

  private static readStoredSelection(key: string): StoredSelection | null {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as StoredSelection;
    } catch {
      return null;
    }
  }

  ngAfterViewInit(): void {
    // Reflect any persisted selections in the autocomplete fields so their labels show on reopen
    const trial = ExpenseFiltersComponent.readStoredSelection(ExpenseFiltersComponent.TrialStorageKey);
    if (trial) {
      this.trialAutocomplete.setInitialValue(trial.id, trial.label);
    }

    const patient = ExpenseFiltersComponent.readStoredSelection(ExpenseFiltersComponent.PatientStorageKey);
    if (patient) {
      this.patientAutocomplete.setInitialValue(patient.id, patient.label);
    }

    const wip = ExpenseFiltersComponent.readStoredSelection(ExpenseFiltersComponent.WipStorageKey);
    if (wip) {
      this.wipAutocomplete.setInitialValue(wip.id, wip.label);
    }
  }

  open(): void {
    this.filtersModal.show();
  }

  getSelectedCount(): number {
    let count = 0;

    if (this.filterForm.controls.preloads.value)
    {
      count++;
    }

    if (this.filterForm.controls.investigations.value)
    {
      count++;
    }

    if (this.filterForm.controls.onhold.value)
    {
      count++;
    }

    if (this.filterForm.controls.trial.value)
    {
      count++;
    }

    if (this.filterForm.controls.patient.value)
    {
      count++;
    }

    if (this.filterForm.controls.wipBy.value)
    {
      count++;
    }

    return count;
  }

  onCheckboxChanged(): void {
    localStorage.setItem('preloads', this.filterForm.controls.preloads.value.toString());
    localStorage.setItem('investigations', this.filterForm.controls.investigations.value.toString());
    localStorage.setItem('on_hold', this.filterForm.controls.onhold.value.toString());
  }

  onTrialChanged(option: TrialAutocomplete | null): void {
    this.persistSession(ExpenseFiltersComponent.TrialStorageKey, option ? { id: option.id, label: option.label } : null);
  }

  onPatientChanged(option: SelectOption | null): void {
    this.persistSession(ExpenseFiltersComponent.PatientStorageKey, option ? { id: option.value, label: option.text } : null);
  }

  onWipChanged(option: SelectOption | null): void {
    this.persistSession(ExpenseFiltersComponent.WipStorageKey, option ? { id: option.value, label: option.text } : null);
  }

  applyFilters(): void {
    this.emitChange();
    this.filtersModal.hide();
  }

  /**
   * Populates the Trial autocomplete (used by the trial filter icon in an expense row)
   */
  setTrial(id: string, label: string): void {
    this.trialAutocomplete.setInitialValue(id, label);
    this.filterForm.controls.trial.setValue(id ?? '');
    this.persistSession(ExpenseFiltersComponent.TrialStorageKey, id ? { id, label } : null);
    this.emitChange();
  }

  /**
   * Populates the Patient autocomplete (used by the patient filter icon in an expense row)
   */
  setPatient(id: string, label: string): void {
    this.patientAutocomplete.setInitialValue(id, label);
    this.filterForm.controls.patient.setValue(id ?? '');
    this.persistSession(ExpenseFiltersComponent.PatientStorageKey, id ? { id, label } : null);
    this.emitChange();
  }

  clearAll(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.filterForm.controls.preloads.setValue(false);
    this.filterForm.controls.investigations.setValue(false);
    this.filterForm.controls.onhold.setValue(false);
    this.filterForm.controls.trial.setValue('');
    this.filterForm.controls.patient.setValue('');
    this.filterForm.controls.wipBy.setValue('');

    this.trialAutocomplete.selected = null;
    this.trialAutocomplete.displayValue = '';
    this.patientAutocomplete.reset();
    this.wipAutocomplete.reset();

    localStorage.setItem('preloads', 'false');
    localStorage.setItem('investigations', 'false');
    localStorage.setItem('on_hold', 'false');
    sessionStorage.removeItem(ExpenseFiltersComponent.TrialStorageKey);
    sessionStorage.removeItem(ExpenseFiltersComponent.PatientStorageKey);
    sessionStorage.removeItem(ExpenseFiltersComponent.WipStorageKey);

    this.emitChange();
  }

  private persistSession(key: string, selection: StoredSelection | null): void {
    if (selection?.id)
    {
      sessionStorage.setItem(key, JSON.stringify(selection));
    }
    else
    {
      sessionStorage.removeItem(key);
    }
  }

  private emitChange(): void {
    this.filtersChanged.emit({
      preloads: this.filterForm.controls.preloads.value,
      investigations: this.filterForm.controls.investigations.value,
      onhold: this.filterForm.controls.onhold.value,
      trialId: this.filterForm.controls.trial.value ?? '',
      patientId: this.filterForm.controls.patient.value ?? '',
      wipActionedById: this.filterForm.controls.wipBy.value ?? ''
    });
  }
}
